import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Clock, MessageSquare } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { AdminPartnerMessage, AdminPartnerThread } from "./types";

function safeDate(value: string | null | undefined, format: "datetime" | "date" = "datetime"): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return format === "date" ? d.toLocaleDateString() : d.toLocaleString();
}

export function AdminPartnerMessagesPanel() {
  const [selectedThread, setSelectedThread] = useState<number | null>(null);

  const threadsQuery = useQuery<AdminPartnerThread[]>({
    queryKey: ["/api/admin/partner-threads"],
  });

  const messagesQuery = useQuery<AdminPartnerMessage[]>({
    queryKey: ["/api/admin/partner-threads", selectedThread, "messages"],
    enabled: selectedThread !== null,
    queryFn: () =>
      fetch(`/api/admin/partner-threads/${selectedThread}/messages`, { credentials: "include" }).then((r) => r.json()),
  });

  const threads = threadsQuery.data ?? [];

  if (selectedThread !== null) {
    const thread = threads.find((t) => t.id === selectedThread);
    const messages = messagesQuery.data ?? [];
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setSelectedThread(null)}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              All threads
            </Button>
            <div>
              <p className="font-semibold text-sm">{thread?.subject || "Thread"}</p>
              <p className="text-xs text-muted-foreground">{thread?.appUserName || "User"}{thread?.propertyAddress ? ` · ${thread.propertyAddress}` : ""}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-96 overflow-y-auto border rounded-lg p-3 bg-muted/20">
            {messagesQuery.isLoading ? (
              <div className="flex justify-center py-8"><Skeleton className="h-6 w-32" /></div>
            ) : messages.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-8">No messages</p>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.senderType === "partner" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-sm rounded-lg px-3 py-2 ${msg.senderType === "partner" ? "bg-blue-100 text-blue-900" : "bg-white border text-slate-900"}`}>
                    <p className="text-xs font-medium mb-0.5 text-muted-foreground">{msg.senderName}</p>
                    <p className="text-sm">{msg.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">{safeDate(msg.createdAt)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2 italic">Admin view is read-only. Conversations are monitored by PadPulse for quality and compliance.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Partner Message Threads
        </CardTitle>
        <CardDescription>Read-only view of all partner ↔ client conversations.</CardDescription>
      </CardHeader>
      <CardContent>
        {threadsQuery.isLoading ? (
          <div className="space-y-3">{[1,2,3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : threads.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No partner message threads yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedThread(t.id)}
                data-testid={`row-partner-thread-${t.id}`}
                className="w-full text-left border rounded-lg p-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{t.appUserName || "User"}</p>
                    {t.subject && <p className="text-xs text-muted-foreground truncate">{t.subject}</p>}
                    {t.lastMessagePreview && <p className="text-xs text-muted-foreground/70 truncate mt-0.5">{t.lastMessagePreview}</p>}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <Clock className="h-3 w-3" />
                    {safeDate(t.lastMessageAt, "date")}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
